import { createSlice } from '@reduxjs/toolkit'
import {
  getEventList,
  getTrackInfo,
  getTrackServiceByTrackId,
} from 'api/axios/crux/bcon'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'
import {
  linkTrackRight,
  unlinkTrackRight,
} from 'features/crux/tracker/list/trackerListSlice'
import {
  cancelTrackMember,
  requestTrackMember,
} from 'features/crux/tracker/modal/request/requestTrackSlice'

const initialInfo = {
  trackId: null,
  name: '',
  population: null,
  createdAt: null,
  lastEventTs: null,
  adAccountLinkStatus: null,
  requestId: null,
}
const initialState = {
  info: initialInfo,
  loaded: false,
  isLoading: false,
  isSubmitting: false,
  isEmpty: false,
  linkTab: 'services',
  usageTab: 'campaigns',
  services: {
    content: [],
    totalElements: 0,
    isLoading: false,
  },
  events: {
    content: [],
    totalElements: 0,
    isLoading: false,
  },
  campaigns: {
    content: [],
    page: 0,
    totalElements: 0,
    isLoading: false,
  },
  adGroups: {
    content: [],
    page: 0,
    totalElements: 0,
    isLoading: false,
  },
}
export const fetchTrackInfo = createAxiosThunk(
  'trackerDetail/fetchTrackInfo',
  (trackId, { getState }) => {
    const adAccountId = getState().account.current.id
    return getTrackInfo({ trackId, adAccountId })
  }
)
export const fetchTrackEvents = createAxiosThunk(
  'trackerDetail/fetchTrackEvents',
  (trackId, { getState }) => {
    const adAccountId = getState().account.current.id
    return getEventList({ adAccountId, trackId })
  }
)
export const fetchTrackServices = createAxiosThunk(
  'trackerDetail/fetchTrackServices',
  (trackId, { getState }) => {
    const adAccountId = getState().account.current.id
    return getTrackServiceByTrackId({ adAccountId, trackId })
  }
)
export const fetchTrackCampaigns = createAxiosThunk(
  'trackerDetail/fetchTrackCampaigns',
  (trackId, { getState }) => {
    const { page } = getState().tracker.detail.campaigns
    return api.tracker.getTrackerCampaigns({ trackId, page })
  }
)
export const fetchTrackAdGroups = createAxiosThunk(
  'trackerDetail/fetchTrackAdGroups',
  (trackId, { getState }) => {
    const { page } = getState().tracker.detail.adGroups
    return api.tracker.getTrackerAdGroups({ trackId, page })
  }
)
const trackerDetailSlice = createSlice({
  name: 'trackerDetail',
  initialState,

  reducers: {
    reset: () => initialState,
    setLinkTab: (state, { payload }) => {
      state.linkTab = payload
    },
    setUsageTab: (state, { payload }) => {
      state.usageTab = payload
    },
    setCampaignsPage: (state, { payload }) => {
      state.campaigns.page = payload
    },
    setAdGroupsPage: (state, { payload }) => {
      state.adGroups.page = payload
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchTrackInfo.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchTrackInfo.fulfilled,
      (state, { payload: { body } }) => {
        if (body) {
          const { deletedAt } = body
          if (deletedAt) {
            state.info = initialInfo
            state.isEmpty = true
          } else {
            state.info = body
            state.loaded = true
          }
        } else {
          state.info = initialInfo
          state.isEmpty = true
        }
        state.isLoading = false
      }
    )

    builder.addCase(fetchTrackInfo.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(fetchTrackServices.pending, state => {
      state.services.isLoading = true
    })

    builder.addCase(
      fetchTrackServices.fulfilled,
      (state, { payload: { body } }) => {
        const { trackRights = [] } = body
        state.services.content = trackRights
        state.services.totalElements = trackRights.length
        state.services.isLoading = false
      }
    )

    builder.addCase(fetchTrackServices.rejected, state => {
      state.services.isLoading = false
    })

    builder.addCase(fetchTrackEvents.pending, state => {
      state.events.isLoading = true
    })

    builder.addCase(
      fetchTrackEvents.fulfilled,
      (state, { payload: { body } }) => {
        state.events.content = body || []
        state.events.totalElements = body?.length || 0
        state.events.isLoading = false
      }
    )

    builder.addCase(fetchTrackEvents.rejected, state => {
      state.events.isLoading = false
    })

    builder.addCase(fetchTrackCampaigns.pending, state => {
      state.campaigns.isLoading = true
    })

    builder.addCase(
      fetchTrackCampaigns.fulfilled,
      (state, { payload: { campaigns } }) => {
        const { content = [], totalElements = 0 } = campaigns
        state.campaigns.content = content
        state.campaigns.totalElements = totalElements
        state.campaigns.isLoading = false
      }
    )

    builder.addCase(fetchTrackCampaigns.rejected, state => {
      state.campaigns.isLoading = false
    })

    builder.addCase(fetchTrackAdGroups.pending, state => {
      state.adGroups.isLoading = true
    })

    builder.addCase(
      fetchTrackAdGroups.fulfilled,
      (state, { payload: { adGroups } }) => {
        const { content = [], totalElements = 0 } = adGroups
        state.adGroups.content = content
        state.adGroups.totalElements = totalElements
        state.adGroups.isLoading = false
      }
    )

    builder.addCase(fetchTrackAdGroups.rejected, state => {
      state.adGroups.isLoading = false
    })

    builder.addCase(requestTrackMember.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(requestTrackMember.fulfilled, state => {
      state.info.role = 'REQUEST'
      state.isSubmitting = false
    })

    builder.addCase(requestTrackMember.rejected, state => {
      state.isSubmitting = false
    })

    builder.addCase(cancelTrackMember.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(cancelTrackMember.fulfilled, state => {
      delete state.info.role
      state.isSubmitting = false
    })

    builder.addCase(cancelTrackMember.rejected, state => {
      state.isSubmitting = false
    })

    builder.addCase(linkTrackRight.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(linkTrackRight.fulfilled, state => {
      state.info.adAccountLinkStatus = 'LINKED'
      state.isSubmitting = false
    })

    builder.addCase(linkTrackRight.rejected, state => {
      state.isSubmitting = false
    })

    builder.addCase(unlinkTrackRight.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(unlinkTrackRight.fulfilled, state => {
      state.isSubmitting = false
    })

    builder.addCase(unlinkTrackRight.rejected, state => {
      state.isSubmitting = false
    })
  },
})
export const {
  reset,
  setLinkTab,
  setUsageTab,
  setCampaignsPage,
  setAdGroupsPage,
} = trackerDetailSlice.actions
export default trackerDetailSlice.reducer
